/**
 * LiftUtilizationReport Component
 *
 * Displays lift utilization data with a bar chart and table for the
 * period chosen on the reports page (#3859). The period's calendar-day
 * labels are passed to the API as they are; the backend owns the time zone.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { chartTooltipProps } from '@/lib/chartTheme';
import { useLiftUtilization } from '@/hooks/useLifts';

export interface LiftUtilizationReportProps {
  /** First calendar day of the report period, `yyyy-MM-dd` (same value the parent report queries with). Sent unchanged. */
  dateFrom: string;
  /** Last calendar day of the report period, `yyyy-MM-dd`, inclusive. */
  dateTo: string;
}

// Natural order: lift 2 before lift 10, regardless of the API's text ordering.
const identifierCollator = new Intl.Collator('fi', { numeric: true, sensitivity: 'base' });

export function LiftUtilizationReport({ dateFrom, dateTo }: LiftUtilizationReportProps) {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  // The parent's day labels go to the API unchanged; the backend resolves them in the workshop's time zone.
  const { data: report, isLoading } = useLiftUtilization(dateFrom, dateTo);

  // Sorted copy: query-cache data must not be mutated.
  const lifts = useMemo(
    () =>
      [...(report?.data ?? [])].sort(
        (a, b) =>
          identifierCollator.compare(a.identifier, b.identifier) ||
          identifierCollator.compare(a.name, b.name)
      ),
    [report?.data]
  );

  const chartData = lifts.map((lift) => ({
    name: lift.identifier,
    utilization: lift.utilizationPercent,
    jobs: lift.jobCount,
  }));

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('liftUtilization.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('liftUtilization.subtitle')}
      </Typography>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : !report || report.data.length === 0 ? (
        <Alert severity="info">{t('liftUtilization.noData')}</Alert>
      ) : (
        <>
          {/* Overall utilization chip */}
          <Stack direction="row" spacing={2} mb={3} alignItems="center">
            <Typography variant="subtitle2">{t('liftUtilization.overall')}:</Typography>
            <Chip
              label={`${report.overall.utilizationPercent}%`}
              color={report.overall.utilizationPercent > 70 ? 'success' : report.overall.utilizationPercent > 40 ? 'warning' : 'default'}
            />
          </Stack>

          {/* Bar chart */}
          <Box sx={{ width: '100%', height: 300, mb: 3 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} unit="%" />
                <Tooltip
                  {...chartTooltipProps(theme)}
                  formatter={(value: number) => [`${value}%`, t('liftUtilization.utilization')]}
                />
                <Bar dataKey="utilization" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('liftUtilization.liftName')}</TableCell>
                  <TableCell align="right">{t('liftUtilization.availableHours')}</TableCell>
                  <TableCell align="right">{t('liftUtilization.usedHours')}</TableCell>
                  <TableCell align="right">{t('liftUtilization.utilization')}</TableCell>
                  <TableCell align="right">{t('liftUtilization.jobCount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lifts.map((lift) => (
                  <TableRow key={lift.id}>
                    <TableCell>{lift.name} ({lift.identifier})</TableCell>
                    <TableCell align="right">
                      {(lift.totalAvailableMinutes / 60).toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      {(lift.totalUsedMinutes / 60).toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={`${lift.utilizationPercent}%`}
                        color={lift.utilizationPercent > 70 ? 'success' : lift.utilizationPercent > 40 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">{lift.jobCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
